// src/components/Transaction/Ethereum/TxErc20Transfers/TxErc20Transfers.tsx
import React, { useRef, useCallback, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { GridApi, GridReadyEvent } from 'ag-grid-community';
import { useQuery } from '@tanstack/react-query';
import { erc20TransfersColDefs } from './aggrid/columnDefs';
import { useChainAndTxHashParams } from 'src/hooks/routing/useChainAndTxHashParams';
import { useAllAttributions } from 'src/hooks/attributions/useAllAttributions';
import { IERC20Transfer } from 'src/types/transactions';
import { api } from 'src/api';

export const TxErc20Transfers = () => {
  const gridApiRef = useRef<GridApi | null>(null);
  const { chain, txHash } = useChainAndTxHashParams();
  const [visibleAddresses, setVisibleAddresses] = useState<string[]>([]);

  const {
    data: transfers,
    isLoading,
    error,
  } = useQuery<IERC20Transfer[]>({
    queryKey: ['erc20Transfers', chain, txHash],
    queryFn: () => api.blockchain.getEvmERC20TransfersByHash(chain, txHash),
    enabled: !!chain && !!txHash,
  });

  // Batch fetch attributions for only visible addresses
  useAllAttributions(chain, [], visibleAddresses);

  const handleVisibleRows = useCallback((api: GridApi) => {
    const addresses: string[] = [];

    // Get pagination info
    const currentPage = api.paginationGetCurrentPage();
    const pageSize = api.paginationGetPageSize();
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;

    // Get only rows on current page
    const displayedRowCount = api.getDisplayedRowCount();
    const rowsToCheck = Math.min(endIndex, displayedRowCount) - startIndex;

    for (let i = 0; i < rowsToCheck; i++) {
      const rowNode = api.getDisplayedRowAtIndex(startIndex + i);
      if (rowNode?.data) {
        const transfer = rowNode.data as IERC20Transfer;
        // Add both from and to addresses
        if (transfer._from) addresses.push(transfer._from);
        if (transfer._to) addresses.push(transfer._to);
      }
    }

    // Remove duplicates and update state
    const uniqueAddresses = Array.from(new Set(addresses));
    setVisibleAddresses(uniqueAddresses);
  }, []);

  const onGridReady = useCallback((e: GridReadyEvent) => {
    gridApiRef.current = e.api;
  }, []);

  if (error) {
    return (
      <div className="p-4 text-red-600">
        Error loading ERC20 transfers: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      <div className="mb-4">
        <h3 className="text-lg font-semibold">
          ERC20 Token Transfers ({transfers?.length || 0})
        </h3>
      </div>

      <div className="ag-theme-alpine w-full">
        <AgGridReact
          rowData={transfers ?? []}
          columnDefs={erc20TransfersColDefs}
          defaultColDef={{
            sortable: true,
            resizable: false,
            filter: true,
            floatingFilter: true,
          }}
          onGridReady={onGridReady}
          onFirstDataRendered={({ api }) => handleVisibleRows(api)}
          onPaginationChanged={({ api }) => handleVisibleRows(api)}
          onSortChanged={({ api }) => handleVisibleRows(api)}
          overlayLoadingTemplate={`<span class="ag-overlay-loading-center">Loading transfers…</span>`}
          overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">No ERC20 transfers found</span>`}
          suppressCellFocus
          domLayout="autoHeight"
          loading={isLoading}
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100]}
        />
      </div>
    </div>
  );
};
